import { validateRedirectUrl } from '@logto/core-kit';
import { z } from 'zod';
export const oidcModelInstancePayloadGuard = z
    .object({
    userCode: z.string().optional(),
    uid: z.string().optional(),
    grantId: z.string().optional(),
})
    /**
     * Try to use `.passthrough()` if type has been fixed.
     * https://github.com/colinhacks/zod/issues/452
     */
    .catchall(z.unknown());
export const oidcClientMetadataGuard = z.object({
    redirectUris: z
        .string()
        .refine((url) => validateRedirectUrl(url, 'web') || validateRedirectUrl(url, 'mobile'))
        .array(),
    postLogoutRedirectUris: z
        .string()
        .refine((url) => validateRedirectUrl(url, 'web') || validateRedirectUrl(url, 'mobile'))
        .array(),
    backchannelLogoutUri: z.string().url().optional(),
    backchannelLogoutSessionRequired: z.boolean().optional(),
    logoUri: z.string().optional(),
});
export var CustomClientMetadataKey;
(function (CustomClientMetadataKey) {
    CustomClientMetadataKey["CorsAllowedOrigins"] = "corsAllowedOrigins";
    CustomClientMetadataKey["IdTokenTtl"] = "idTokenTtl";
    /** @deprecated Use {@link RefreshTokenTtlInDays} instead. */
    CustomClientMetadataKey["RefreshTokenTtl"] = "refreshTokenTtl";
    CustomClientMetadataKey["RefreshTokenTtlInDays"] = "refreshTokenTtlInDays";
    CustomClientMetadataKey["TenantId"] = "tenantId";
    /**
     * Enabling this configuration will allow Logto to always issue Refresh Tokens, regardless of whether `prompt=consent` is presented in the authentication request.
     *
     * It only works for web applications when the client allowed grant types includes `refresh_token`.
     *
     * This config is for the third-party integrations that do not strictly follow OpenID Connect standards due to some reasons (e.g. they only know OAuth, but requires a Refresh Token to be returned anyway).
     */
    CustomClientMetadataKey["AlwaysIssueRefreshToken"] = "alwaysIssueRefreshToken";
    /**
     * When enabled (default), Logto will issue a new Refresh Token for token requests when 70% of the original Time to Live (TTL) has passed.
     *
     * It can be turned off for only traditional web apps for enhanced security.
     */
    CustomClientMetadataKey["RotateRefreshToken"] = "rotateRefreshToken";
    /**
     * Whether the application is allowed to initiate token exchange requests.
     *
     * Only first-party applications can use token exchange. Third-party applications are always
     * forbidden.
     *
     * Defaults to `false` for all new applications. Users must explicitly enable it.
     */
    CustomClientMetadataKey["AllowTokenExchange"] = "allowTokenExchange";
    /**
     * Whether the application uses the OAuth 2.0 Device Authorization Grant (RFC 8628)
     * instead of the standard Authorization Code flow.
     *
     * Only applicable to native applications. Defaults to `false`.
     */
    CustomClientMetadataKey["IsDeviceFlow"] = "isDeviceFlow";
    /**
     * The maximum number of active sessions (devices) allowed per user for this application.
     *
     * When exceeded, old sessions should be revoked according to server policy.
     */
    CustomClientMetadataKey["MaxAllowedGrants"] = "maxAllowedGrants";
    /**
     * Default ACR (Authentication Context Class Reference) values to request for this application.
     *
     * When set, Logto will automatically include these values in the effective `acr_values` for
     * authorization requests originating from this app (unless the client explicitly overrides them).
     *
     * Use {@link LogtoAcr} for the supported values, e.g. `['urn:logto:acr:mfa']` to
     * require MFA by default for all users of this application.
     *
     * @see {@link https://www.rfc-editor.org/rfc/rfc9470 | RFC 9470 Step-Up Authentication}
     */
    CustomClientMetadataKey["DefaultAcrValues"] = "defaultAcrValues";
})(CustomClientMetadataKey || (CustomClientMetadataKey = {}));
/**
 * The application APIs validate `customClientMetadata` with `.strict()` (see `applicationCreateGuard`),
 * so the set of keys below is part of the public API contract:
 *
 * - Never remove or rename an existing key: a client that still sends it would go from 200 to 400.
 *   Keep it and mark it `@deprecated` instead, the way {@link CustomClientMetadataKey.RefreshTokenTtl}
 *   is handled.
 * - New keys must be `.optional()`: a required key would reject every existing create or update
 *   payload that sends `customClientMetadata` without it, and PATCH replaces the whole object.
 */
export const customClientMetadataGuard = z.object({
    [CustomClientMetadataKey.CorsAllowedOrigins]: z.string().min(1).array().optional(),
    [CustomClientMetadataKey.IdTokenTtl]: z.number().optional(),
    [CustomClientMetadataKey.RefreshTokenTtl]: z.number().optional(),
    [CustomClientMetadataKey.RefreshTokenTtlInDays]: z.number().int().min(1).max(180).optional(),
    [CustomClientMetadataKey.TenantId]: z.string().optional(),
    [CustomClientMetadataKey.AlwaysIssueRefreshToken]: z.boolean().optional(),
    [CustomClientMetadataKey.RotateRefreshToken]: z.boolean().optional(),
    [CustomClientMetadataKey.AllowTokenExchange]: z.boolean().optional(),
    [CustomClientMetadataKey.IsDeviceFlow]: z.boolean().optional(),
    [CustomClientMetadataKey.MaxAllowedGrants]: z.number().int().positive().optional(),
    [CustomClientMetadataKey.DefaultAcrValues]: z.string().array().optional(),
});
export const oidcSessionAuthorizationDetailsGuard = z
    .object({
    /**
     * The `sid` (session ID) Claim associated with the session for the current client.
     *
     * @remarks
     * Mark optional to make the guard more robust.
     * Should always be present in the session authorization details
     */
    sid: z.string().optional(),
    /**
     * The grantId associated with the session for the current client.
     *
     * @remarks
     * Mark optional to make the guard more robust.
     * Should always be present in the session authorization details when the session is authorized with a grant.
     */
    grantId: z.string().optional(),
    /**
     * Whether the grant associated with the session should be persisted after the session is terminated.
     *
     * @remarks
     * Mark optional to make the guard more robust.
     */
    persistsLogout: z.boolean().optional(),
})
    .catchall(z.unknown());
export const oidcSessionInstancePayloadGuard = z
    .object({
    exp: z.number(),
    iat: z.number(),
    jti: z.string(),
    uid: z.string(),
    kind: z.literal('Session'),
    loginTs: z.number(),
    accountId: z.string(),
    /**
     * A map of client_id to session authorization details. @see OidcSessionAuthorizationDetails
     */
    authorizations: z.record(z.string(), oidcSessionAuthorizationDetailsGuard).optional(),
})
    .catchall(z.unknown());
